import {createLiveSessionScope} from "../context/createLiveSessionScope";
import {useResolvedPermissionOrgId} from "../context/OrganizationPermissionScope";
import {resolvePublicOrganizationId} from "../services/publicOrganizationReader";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db, functions as firebaseFunctions } from "../firebaseConfig";
import { isChurchCatalogRoleId, resolveCatalogRolePermissions } from "../config/roles/churchRoleCatalog";

export const PERMISSION_KEYS = [
  "canManageRoles",
  "canManageTeams",
  "canManageTeamRoster",
  "canEditProfile",
  "canManageServices",
  "canPublishServicePrograms",
  "canViewDashboard",
  "canManagePeople",
  "canManageHouseholds",
  "canManageAttendance",
  "canManageChildren",
  "canManageChildrenCheckin",
  "canCheckInChildren",
  "canCheckOutChildren",
  "canManageGiving",
  "canApproveDonations",
  "canRecordOfflineGifts",
  "canApprovePayments",
  "canRecordManualPayments",
  "canManagePayments",
  "canManageStripe",
  "canManagePayouts",
  "canManageAutomations",
  "canManageFunding",
  "canManageReceipts",
  "canEditWebsite",
  "canManageEvents",
  "canUploadMedia",
  "canManageMedia",
  "canViewReports",
  "canManageCampaigns",
  "canManageFundraising",
  "canApproveCampaigns",
];

export const EMPTY_PERMISSIONS = Object.fromEntries(PERMISSION_KEYS.map((key) => [key, false]));

export const ROLE_PERMISSIONS = {
  owner: Object.fromEntries(PERMISSION_KEYS.map((key) => [key, true])),
  admin: {
    ...EMPTY_PERMISSIONS,
    canManageRoles: true,
    canManageTeams: true,
    canEditProfile: true,
    canManageServices: true,
    canViewDashboard: true,
    canManagePeople: true,
    canManageHouseholds: true,
    canManageAttendance: true,
    canManageChildren: true,
    canCheckInChildren: true,
    canCheckOutChildren: true,
    canManageGiving: true,
    canManageFunding: true,
    canManageAutomations: true,
    canApproveDonations: true,
    canRecordOfflineGifts: true,
    canApprovePayments: true,
    canRecordManualPayments: true,
    canManageReceipts: true,
    canManageEvents: true,
    canUploadMedia: true,
    canManageMedia: true,
    canViewReports: true,
    canManageCampaigns: true,
    canManageFundraising: true,
    canApproveCampaigns: true,
  },
  finance_admin: {
    ...EMPTY_PERMISSIONS,
    canViewDashboard: true,
    canManageGiving: true,
    canApproveDonations: true,
    canRecordOfflineGifts: true,
    canApprovePayments: true,
    canRecordManualPayments: true,
    canManagePayments: true,
    canManageReceipts: true,
    canViewReports: true,
  },
  campaign_admin: {
    ...EMPTY_PERMISSIONS,
    canViewDashboard: true,
    canManageCampaigns: true,
    canManageFundraising: true,
    canApproveCampaigns: true,
    canManageGiving: true,
    canViewReports: true,
  },
  campaign_approver: {
    ...EMPTY_PERMISSIONS,
    canViewDashboard: true,
    canManageCampaigns: true,
    canManageFundraising: true,
    canApproveCampaigns: true,
    canViewReports: true,
  },
  content_admin: {
    ...EMPTY_PERMISSIONS,
    canViewDashboard: true,
    canEditProfile: true,
    canEditWebsite: true,
    canManageServices: true,
    canManageEvents: true,
    canUploadMedia: true,
    canManageMedia: true,
  },
  media_manager: {
    ...EMPTY_PERMISSIONS,
    canViewDashboard: true,
    canUploadMedia: true,
    canManageMedia: true,
    canManageEvents: true,
  },
  communications: {
    ...EMPTY_PERMISSIONS,
    canViewDashboard: true,
    canEditWebsite: true,
    canUploadMedia: true,
    canManageMedia: true,
    canManageEvents: true,
  },
  editor: {
    ...EMPTY_PERMISSIONS,
    canViewDashboard: true,
    canEditProfile: true,
    canEditWebsite: true,
    canUploadMedia: true,
    canManageMedia: true,
    canManageEvents: true,
  },
  ministry_leader: {
    ...EMPTY_PERMISSIONS,
    canViewDashboard: true,
    canManageEvents: true,
    canUploadMedia: true,
  },
  department_leader: {
    ...EMPTY_PERMISSIONS,
    canViewDashboard: true,
    canManageEvents: true,
    canUploadMedia: true,
  },
  pastor: {
    ...EMPTY_PERMISSIONS,
    canPublishServicePrograms: true,
    canViewDashboard: true,
    canManagePeople: true,
    canManageHouseholds: true,
    canManageAttendance: true,
    canViewReports: true,
  },
  shepherd: {
    ...EMPTY_PERMISSIONS,
    canViewDashboard: true,
    canManagePeople: true,
    canManageHouseholds: true,
    canManageAttendance: true,
    canViewReports: true,
  },
  care_leader: {
    ...EMPTY_PERMISSIONS,
    canViewDashboard: true,
    canManagePeople: true,
    canManageHouseholds: true,
    canViewReports: true,
  },
  attendance_admin: {
    ...EMPTY_PERMISSIONS,
    canViewDashboard: true,
    canManageAttendance: true,
    canManageChildrenCheckin: true,
    canCheckInChildren: true,
    canCheckOutChildren: true,
  },
  volunteer: {
    ...EMPTY_PERMISSIONS,
    canViewDashboard: true,
    canCheckInChildren: true,
  },
  viewer: {
    ...EMPTY_PERMISSIONS,
    canViewDashboard: true,
  },
};

const PermissionsContext = createContext(new Map());
const PERMISSIONS_CACHE_TTL_MS = 5 * 60 * 1000;
export const AUTHORIZATION_TIMEOUT_MS = 12 * 1000;
export const AUTHORIZATION_STATES = Object.freeze({
  LOADING: "loading",
  ALLOWED: "allowed",
  DENIED: "denied",
  ERROR: "error",
});

function clean(value) {
  return String(value || "").trim();
}

export function isActiveMemberRecord(member = {}) {
  const flags = member?.flags && typeof member.flags === "object" ? member.flags : {};
  if (member?.archived === true || member?.isArchived === true || flags.isArchived === true) {
    return false;
  }
  const status = clean(member?.status).toLowerCase();
  return status === "active" || status === "enabled";
}

function cacheKey(orgId, uid) {
  return `lightly:permissions:${orgId}:${uid}`;
}

function readPermissionCache(key) {
  try {
    const cached = JSON.parse(localStorage.getItem(key) || "null");
    const cachedAt = Number(cached?.cachedAt || 0);
    // Permission cache is only a short-lived bootstrap for PWA startup.
    // Stale cache falls back to minimal permissions until Firestore confirms role.
    if (!cachedAt || Date.now() - cachedAt > PERMISSIONS_CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return cached;
  } catch {
    return null;
  }
}

export function hasAssignedAccessGrant(memberData = {}) {
  const role = clean(memberData.role).toLowerCase();
  const permissions =
    memberData.permissions && typeof memberData.permissions === "object"
      ? memberData.permissions
      : {};
  return Boolean(role && role !== "member") ||
    Object.values(permissions).some((value) => value === true);
}

function normalizePermissions(role, permissions = {}) {
  // Church catalog roles resolve strictly: defaults + stored grants, minus
  // forbidden permissions, with NO implied expansions (check-in never implies
  // check-out). Legacy roles below keep their existing compatibility behavior.
  if (isChurchCatalogRoleId(role)) {
    return resolveCatalogRolePermissions(role, permissions);
  }
  return normalizeLegacyPermissions(role, permissions);
}

// Pre-catalog permission behavior, including its implicit expansions.
// Kept intact for legacy roles and exported for migration diffing.
function normalizeLegacyPermissions(role, permissions = {}) {
  const roleDefaults = ROLE_PERMISSIONS[role] || {};
  const merged = {
    ...EMPTY_PERMISSIONS,
    ...roleDefaults,
    ...(permissions || {}),
  };
  if (role === "owner" || role === "admin") {
    Object.entries(roleDefaults).forEach(([key, value]) => {
      if (value === true) merged[key] = true;
    });
  }
  merged.canEditWebsite = Boolean(merged.canEditWebsite || merged.canEditProfile);
  merged.canEditProfile = Boolean(merged.canEditProfile || merged.canEditWebsite);
  merged.canManageStripe = Boolean(merged.canManageStripe || merged.canManagePayments);
  merged.canManagePayments = Boolean(merged.canManagePayments || merged.canManageStripe);
  merged.canApprovePayments = Boolean(merged.canApprovePayments || merged.canManagePayments);
  merged.canRecordManualPayments = Boolean(merged.canRecordManualPayments || merged.canManagePayments);
  merged.canManageReceipts = Boolean(merged.canManageReceipts || merged.canManageGiving);
  merged.canManageCampaigns = Boolean(merged.canManageCampaigns || merged.canApproveCampaigns);
  merged.canManageFundraising = Boolean(merged.canManageFundraising || merged.canManageCampaigns);
  merged.canManageChildrenCheckin = Boolean(
    merged.canManageChildrenCheckin ||
      merged.canManageChildren ||
      merged.canCheckInChildren ||
      merged.canCheckOutChildren
  );
  merged.canCheckInChildren = Boolean(merged.canCheckInChildren || merged.canManageChildrenCheckin);
  merged.canCheckOutChildren = Boolean(merged.canCheckOutChildren || merged.canManageChildrenCheckin);
  return merged;
}

async function resolveOrgId(orgIdOrSlug) {
  const raw = clean(orgIdOrSlug);
  if (!raw) return {orgId: "", lookupFailed: false};
  try { return {orgId: await resolvePublicOrganizationId(raw) || raw, lookupFailed: false}; }
  catch { return {orgId: raw, lookupFailed: true}; }
}

function initialPermissionState(user = null, requestKey = "") {
  return {
    requestKey,
    uid: user?.uid || "",
    role: null,
    permissions: EMPTY_PERMISSIONS,
    membership: null,
    user,
    status: null,
    isOwner: false,
    isAdmin: false,
    isMember: false,
    /* Organization-scoped identity link. NOT membership: it grants the person
     * their own profile and giving and nothing else. Only ever set from the
     * server's authoritative access block -- never from a url, an email, local
     * storage, or the mere existence of an auth account. */
    isLinkedPerson: false,
    linkedPersonId: "",
    canEditProfile: false,
    isCustomRole: false,
    loading: true,
    authorizationStatus: AUTHORIZATION_STATES.LOADING,
    authorizationError: "",
    orgId: "",
    can: () => false,
  };
}

/**
 * Drop every cached access decision.
 *
 * usePermissions short-circuits on a cached entry within its TTL, so after
 * something changes what access SHOULD be -- verifying an email, then linking --
 * a stale "denied" would otherwise survive the re-check and the member would
 * stay locked out until the TTL expired.
 */
export function invalidatePermissionsCache() {
  window.dispatchEvent(new Event("lightly:permissions-invalidated"));
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith("lightly:permissions:"))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage is optional; the in-memory cache is cleared by the remount.
  }
}

export function PermissionsProvider({ children }) {
  const [cache, setCache] = useState(new Map());
  const [sessionUid, setSessionUid] = useState(auth.currentUser?.uid || "");
  useEffect(() => onAuthStateChanged(auth, user => {
    setSessionUid(user?.uid || "");
  }), []);

  const value = useMemo(() => ({ cache, setCache }), [cache]);
  return <PermissionsContext.Provider value={value}><permissionSession.Provider key={sessionUid}>{children}</permissionSession.Provider></PermissionsContext.Provider>;
}

function usePermissionSource(orgIdOrSlug, { enabled = true, requireServerSnapshots = false } = {}) {
  const context = useContext(PermissionsContext);
  const contextRef = useRef(context);
  const [authUser, setAuthUser] = useState(enabled ? auth.currentUser || null : null);
  const [authResolved, setAuthResolved] = useState(false);
  const [orgId, setOrgId] = useState("");
  const [orgLookupFailed, setOrgLookupFailed] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const failureCount = useRef(0);
  const requestKey = clean(orgIdOrSlug);
  const resolvedScopeOrgId = useResolvedPermissionOrgId(requestKey);
  const [state, setState] = useState(() => initialPermissionState(auth.currentUser || null, requestKey));

  useEffect(() => {
    contextRef.current = context;
  }, [context]);

  useEffect(() => enabled ? onAuthStateChanged(auth, (user) => {
    setAuthUser(user);
    setAuthResolved(true);
    if (!user) {
      contextRef.current?.setCache?.(new Map());
      try {
        Object.keys(localStorage)
          .filter((key) => key.startsWith("lightly:permissions:"))
          .forEach((key) => localStorage.removeItem(key));
      } catch {
        // Storage is optional; signed-out state is still quarantined in memory.
      }
    }
  }) : undefined, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let finished = false;
    setOrgId("");
    setOrgLookupFailed(false);
    setState(initialPermissionState(authUser, requestKey));
    const timeoutId = window.setTimeout(() => {
      if (cancelled || finished) return;
      finished = true;
      setState({
        ...initialPermissionState(authUser, requestKey),
        loading: false,
        authorizationStatus: AUTHORIZATION_STATES.ERROR,
        authorizationError: "We couldn't verify your access. Please retry.",
      });
    }, AUTHORIZATION_TIMEOUT_MS);
    const resolution = resolvedScopeOrgId ? Promise.resolve({orgId:resolvedScopeOrgId,lookupFailed:false}) : resolveOrgId(orgIdOrSlug);
    resolution.then((resolved) => {
      if (!cancelled && !finished) {
        finished = true;
        window.clearTimeout(timeoutId);
        setOrgId(resolved.orgId);
        setOrgLookupFailed(resolved.lookupFailed === true);
      }
    });
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [orgIdOrSlug, resolvedScopeOrgId, retryNonce, enabled, authUser, requestKey]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!authResolved || !orgId || !authUser?.uid) {
      setState((current) => ({
        ...initialPermissionState(authUser, requestKey),
        authorizationStatus: authResolved && !authUser?.uid
          ? AUTHORIZATION_STATES.DENIED
          : AUTHORIZATION_STATES.LOADING,
        loading: !(authResolved && !authUser?.uid),
        orgId,
      }));
      return undefined;
    }

    const key = cacheKey(orgId, authUser.uid);
    const permissionContext = contextRef.current;
    const memoryCached = permissionContext?.cache?.get?.(key);
    const cachedAt = Number(memoryCached?.cachedAt || 0);
    const cached = memoryCached && Date.now() - cachedAt <= PERMISSIONS_CACHE_TTL_MS
      ? memoryCached
      : readPermissionCache(key);

    if (cached) {
      setState({
        ...cached,
        requestKey,
        loading: true,
        authorizationStatus: AUTHORIZATION_STATES.LOADING,
        authorizationError: "",
      });
    } else {
      setState(initialPermissionState(authUser, requestKey));
    }

    const orgRef = doc(db, "organizations", orgId, "accessContext", "current");
    const memberRef = doc(db, "organizations", orgId, "members", authUser.uid);
    let orgData = null;
    let memberData = null;
    let orgResolved = false;
    let memberResolved = false;
    let orgServerConfirmed = false;
    let memberServerConfirmed = false;
    let failed = false;
    let active = true;
    let timeoutId = null;
    let retryTimer = null;

    // A slow answer (timeout) is recoverable: a later snapshot still publishes, so a
    // late server confirmation never leaves admin actions hidden for the session. A
    // listener error ends that listener, so it is retried shortly afterwards.
    const publishError = ({recoverable = false} = {}) => {
      if (!active || failed) return;
      if (!recoverable) {
        failed = true;
        const attempt = Math.min(failureCount.current, 3);
        failureCount.current += 1;
        retryTimer = window.setTimeout(() => { if (active) setRetryNonce((value) => value + 1); }, 5000 * 3 ** attempt);
      }
      setState({
        ...initialPermissionState(authUser, requestKey),
        loading: false,
        authorizationStatus: AUTHORIZATION_STATES.ERROR,
        authorizationError: "We couldn't verify your access. Please retry.",
        orgId,
      });
    };

    /* The identity link is resolved from getMemberProfileForCurrentUser,
     * which is the authority on it. Cached per publish cycle so a re-render
     * cannot re-ask, and only consulted for callers who are NOT active members.
     *
     * null = not asked yet, so the guard keeps waiting rather than rendering a
     * denial it may be about to contradict. */
    let linkResolved = false;
    let linkAccess = null;
    let linkOutcome = "";

    const resolveIdentityLink = async () => {
      try {
        const getProfile = httpsCallable(firebaseFunctions, "getMemberProfileForCurrentUser");
        /* accessCheckOnly is the read-only mode: it evaluates access that
         * already exists and writes nothing -- no member, no pending claim, no
         * identity link, no audit, no canonicalisation. claimOnly alone is NOT
         * enough (it suppresses member creation only), and this runs on every
         * protected route for a non-member, so it must not mutate. */
        /* claimOnly is sent ALONGSIDE accessCheckOnly deliberately. During a
         * rollout an older function revision would ignore accessCheckOnly, and
         * without claimOnly it would take the mutating no-match path and create
         * a member. Functions deploy before Hosting for the same reason; this
         * makes the window safe even if that order is ever broken. */
        const res = await getProfile({ orgId, accessCheckOnly: true, claimOnly: true });
        linkAccess = res?.data?.access || null;
        linkOutcome = res?.data?.accessOutcome || "";
      } catch {
        /* Fail closed: no link, therefore no access. An error here must never
         * read as "linked" -- but it is also not proof of no relationship, so
         * it is reported as temporarily unavailable rather than a refusal. */
        linkAccess = null;
        linkOutcome = "temporarily_unavailable";
      }
      linkResolved = true;
      publish();
    };

    const publish = () => {
      if (!active || failed || !orgResolved || !memberResolved) return;
      if (!orgData && orgLookupFailed) {
        publishError();
        return;
      }
      const isLegacyOwner = orgData?.ownerUserId === authUser.uid;
      const data = memberData || {};
      const role = isLegacyOwner ? "owner" : clean(data.role).toLowerCase() || null;
      const status = isLegacyOwner ? "active" : clean(data.status).toLowerCase() || (memberData ? "active" : null);
      const permissions = normalizePermissions(role, data.permissions);
      const memberIsActive = isLegacyOwner
        ? (!memberData || isActiveMemberRecord(data))
        : (Boolean(memberData) && isActiveMemberRecord(data));

      permissions.canManageCuisine = permissions.canManageCuisine === true && orgData?.cuisineEnabled === true &&
        Boolean(memberData) && isActiveMemberRecord(data) && clean(data.role).toLowerCase() !== "guest";

      // A missing/denied bootstrap must not silently turn a safety setting off
      // for an active member. Non-members may still obtain a normal refusal or
      // the server's explicitly limited identity-link experience.
      if (memberIsActive && (!orgData || orgData.schemaVersion !== 1 || orgData.organizationId !== orgId ||
          typeof orgData.childSafetyAccessEnforcementEnabled !== "boolean" ||
          ["disabled", "suspended", "deleted", "archived"].includes(orgData.status))) {
        publishError();
        return;
      }

      /* Only a non-member needs the link resolved. Ask once, and hold the
       * LOADING state until the answer arrives so the route guard cannot flash
       * "No access" at somebody who does have linked access. */
      if (!memberIsActive && !linkResolved) {
        resolveIdentityLink();
        return;
      }

      /* A link is real only when the server says so AND names the person. */
      const isLinkedPerson = !memberIsActive &&
        linkAccess?.source === "identity_link" &&
        Boolean(clean(linkAccess?.personId));
      const linkedPersonId = isLinkedPerson ? clean(linkAccess.personId) : "";

      const next = {
        serverConfirmed: orgServerConfirmed && memberServerConfirmed,
        requestKey,
        uid: authUser.uid,
        user: authUser,
        membership: memberData ? { id: authUser.uid, uid: authUser.uid, ...memberData, orgId } : null,
        role,
        status,
        permissions,
        isOwner: role === "owner" && memberIsActive,
        isAdmin: ["owner", "admin"].includes(role) && memberIsActive,
        // Membership and role are independent. Ordinary members commonly have
        // no operator role; canonical active status alone grants member-level
        // access, while roles continue to control administrative capabilities.
        isMember: memberIsActive,
        isLinkedPerson,
        linkedPersonId,
        /* Why access is what it is, so the UI can say something true instead of
         * one refusal for every cause. */
        accessOutcome: memberIsActive ? "active_member" : linkOutcome,
        /* The server decides editability. A member keeps the previous
         * behaviour; a linked non-member is read-only. */
        canEditProfile: memberIsActive || linkAccess?.canEditProfile === true,
        isCustomRole: false,
        loading: false,
        authorizationStatus: memberIsActive ? AUTHORIZATION_STATES.ALLOWED : AUTHORIZATION_STATES.DENIED,
        authorizationError: "",
        orgId,
        organizationIdentity: {organizationType:orgData?.organizationType || '', orgType:orgData?.orgType || '', appTemplateId:orgData?.appTemplateId || ''},
        childSafetyAccessEnforcementEnabled:
          orgData?.childSafetyAccessEnforcementEnabled === true,
        cachedAt: Date.now(),
        can: (permissionName) => Boolean(memberIsActive && permissions?.[permissionName]),
      };

      if (timeoutId && (!requireServerSnapshots || next.serverConfirmed || !memberIsActive)) { window.clearTimeout(timeoutId); timeoutId = null; }
      // A confirmed role can lose its server confirmation later (the connection dropped
      // and snapshots now come from cache). Time that out too, so pages that need a
      // confirmed role show "couldn't verify, try again" instead of "checking" forever.
      if (!timeoutId && requireServerSnapshots && memberIsActive && !next.serverConfirmed) armTimeout();
      if (next.serverConfirmed || !requireServerSnapshots) failureCount.current = 0;
      setState(next);
      contextRef.current?.setCache?.((current) => {
        const copy = new Map(current);
        copy.set(key, next);
        return copy;
      });
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Ignore storage failures.
      }
    };

    function armTimeout() {
      timeoutId = window.setTimeout(() => { timeoutId = null; publishError({recoverable: true}); }, AUTHORIZATION_TIMEOUT_MS);
    }
    armTimeout();

    const subscribe = (ref, next, error) => requireServerSnapshots
      ? onSnapshot(ref, { includeMetadataChanges: true }, next, error)
      : onSnapshot(ref, next, error);
    const unsubOrg = subscribe(
      orgRef,
      (snap) => {
        orgServerConfirmed = snap.metadata?.fromCache === false && snap.metadata?.hasPendingWrites === false;
        orgData = snap.exists() ? snap.data() || {} : null;
        orgResolved = true;
        publish();
      },
      (err) => {
        if (err?.code !== "permission-denied") { publishError(); return; }
        // A non-member cannot read member-only bootstrap data. Their own
        // membership/link result still determines a normal access refusal.
        orgData = {};
        orgResolved = true;
        publish();
      }
    );

    const unsubMember = subscribe(
      memberRef,
      (snap) => {
        memberServerConfirmed = snap.metadata?.fromCache === false && snap.metadata?.hasPendingWrites === false;
        memberData = snap.exists() ? snap.data() || {} : null;
        memberResolved = true;
        publish();
      },
      () => {
        publishError();
      }
    );

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
      window.clearTimeout(retryTimer);
      unsubOrg();
      unsubMember();
    };
  }, [authResolved, authUser, orgId, orgLookupFailed, requestKey, retryNonce, enabled, requireServerSnapshots]);

  useEffect(() => {
    if (!enabled) return undefined;
    const invalidate = () => setRetryNonce(value => value + 1);
    window.addEventListener("lightly:permissions-invalidated", invalidate);
    return () => window.removeEventListener("lightly:permissions-invalidated", invalidate);
  }, [enabled]);

  // Coming back to the tab or the network re-checks access that could not be verified.
  const failedNow = state.authorizationStatus === AUTHORIZATION_STATES.ERROR;
  useEffect(() => {
    if (!enabled || !failedNow) return undefined;
    const resume = () => { if (document.visibilityState !== "hidden") setRetryNonce(value => value + 1); };
    window.addEventListener("focus", resume);
    window.addEventListener("online", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      window.removeEventListener("focus", resume);
      window.removeEventListener("online", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [enabled, failedNow]);

  const currentRequest = state.requestKey === requestKey && (!state.orgId || !orgId || state.orgId === orgId);
  const retry = () => setRetryNonce((value) => value + 1);
  const visibleState = currentRequest ? state : initialPermissionState(authUser, requestKey);

  return {
    ...visibleState,
    user: visibleState.user || authUser,
    retry,
    can: (permissionName) => Boolean(
      visibleState.authorizationStatus === AUTHORIZATION_STATES.ALLOWED &&
      isActiveMemberRecord(visibleState.membership || { status: visibleState.status }) &&
      visibleState.permissions?.[permissionName]
    ),
  };
}

export function mergeRolePermissions(role, explicitPermissions = {}) {
  return normalizePermissions(role, explicitPermissions);
}

// Original pre-catalog resolution regardless of roleId. Used by the legacy
// role compatibility mapper to diff what a person can do today against the
// catalog bundle they would migrate to.
export function mergeLegacyRolePermissions(role, explicitPermissions = {}) {
  return normalizeLegacyPermissions(role, explicitPermissions);
}

const permissionSession = createLiveSessionScope(usePermissionSource);
export function usePermissions(orgIdOrSlug, {enabled = true, requireServerSnapshots = false} = {}) {
  const requestKey = clean(orgIdOrSlug);
  const uid = auth.currentUser?.uid || "";
  // All session consumers share the stricter metadata-aware subscription.
  const shared = permissionSession.useShared(uid + ":" + requestKey,
    [orgIdOrSlug, {requireServerSnapshots: true}], enabled);
  const local = usePermissionSource(orgIdOrSlug, {enabled: enabled && !shared.provided, requireServerSnapshots});
  if (!shared.provided || !enabled) return local;
  const value = shared.value;
  return value && value.uid === uid && value.requestKey === requestKey
    ? value : {...initialPermissionState(auth.currentUser, requestKey), retry: () => {}};
}
